import React, { useState, useEffect } from 'react';
import { Modal, Form, InputNumber, Input, Select, message, Alert, Card, Table, Row, Col, Typography, Tag, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { inventoryService } from '@services/operator/inventoryService';
import { REASON_CODES } from '@constants';

const { Text } = Typography;

/**
 * @param {Object} props
 * @param {boolean} props.visible
 * @param {Object} [props.inventory]
 * @param {Function} props.onCancel
 * @param {Function} props.onConfirm
 * @param {string} [props.defaultReasonCode]
 * @param {Array<Object>} [props.existingItems]
 */
export default function AdjustmentItemModal({ visible, inventory, onCancel, onConfirm, defaultReasonCode, existingItems = [] }) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loadingShelves, setLoadingShelves] = useState(false);
  const [shelfEntries, setShelfEntries] = useState([]);

  useEffect(() => {
    if (visible && inventory) {
      form.resetFields();
      form.setFieldsValue({ reason_code: defaultReasonCode || 'OTHER' });
      fetchShelfInventories();
    } else {
      setShelfEntries([]);
    }
  }, [visible, inventory]);

  if (!inventory) return null;

  const productSku = inventory.product?.sku || inventory.product_sku;
  const productName = inventory.product?.name || inventory.product_name;

  const fetchShelfInventories = async () => {
    setLoadingShelves(true);
    try {
      const response = await inventoryService.getShelfInventories(inventory.id);
      if (response.success) {
        const list = Array.isArray(response.data) ? response.data : (response.data.list || []);
        const usedShelfIds = new Set(
          existingItems.filter(item => item.inventory_id === inventory.id).map(item => item.shelf_id)
        );
        // 盤點調整：不受配置/保留量限制，system_quantity 直接使用儲位實際總量，
        // 差異透過調整來修正（即便有 allocated/held 仍允許更正）。
        const entries = list
          .filter(si => !usedShelfIds.has(si.shelf_id))
          .map(si => ({ ...si, quantity: si.quantity, actualQuantity: si.quantity }));
        setShelfEntries(entries);
      }
    } catch (error) {
      console.error(error);
      message.error(t('common.message.fetch_failed'));
    } finally {
      setLoadingShelves(false);
    }
  };

  const handleQuantityChange = (shelfId, value) => {
    setShelfEntries(prev => prev.map(e =>
      e.shelf_id === shelfId ? { ...e, actualQuantity: value } : e
    ));
  };

  const changedEntries = shelfEntries.filter(e => e.actualQuantity != null && e.actualQuantity !== e.quantity);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();

      if (changedEntries.length === 0) {
        message.error(t('shared.stock_adjustment.error.no_difference'));
        return;
      }

      const items = changedEntries.map(e => ({
        inventory_id: inventory.id,
        inventory: inventory,
        shelf_id: e.shelf_id,
        shelf: e,
        quantity: e.actualQuantity - e.quantity,
        reason_code: values.reason_code,
        note: values.note,
        actual_quantity: e.actualQuantity,
        system_quantity: e.quantity,
      }));

      onConfirm(items);
    } catch (error) {
      console.error(error);
    }
  };

  const shelfColumns = [
    {
      title: t('models.shelf.location'),
      dataIndex: 'shelf_location',
      width: 140,
    },
    {
      title: t('shared.stock_adjustment.system_quantity'),
      dataIndex: 'quantity',
      width: 100,
      align: 'right',
    },
    {
      title: t('shared.stock_adjustment.actual_quantity'),
      width: 130,
      align: 'right',
      render: (_, r) => (
        <InputNumber
          min={0}
          value={r.actualQuantity}
          onChange={(val) => handleQuantityChange(r.shelf_id, val)}
          style={{ width: '100%' }}
          size="small"
        />
      ),
    },
    {
      title: t('shared.stock_adjustment.difference'),
      width: 80,
      align: 'right',
      render: (_, r) => {
        const diff = r.actualQuantity != null ? r.actualQuantity - r.quantity : null;
        if (diff == null || diff === 0) return <Text type="secondary">-</Text>;
        const isGain = diff > 0;
        return (
          <Text strong style={{ color: isGain ? '#52c41a' : '#ff4d4f' }}>
            {isGain ? `+${diff}` : diff}
          </Text>
        );
      },
    },
  ];

  return (
    <Modal
      title={t('shared.stock_adjustment.add_item')}
      open={visible}
      onCancel={onCancel}
      onOk={handleOk}
      okText={t('common.action.confirm')}
      cancelText={t('common.action.cancel')}
      okButtonProps={{ disabled: changedEntries.length === 0 }}
      width={700}
    >
      {/* Product Info */}
      <Card size="small" style={{ marginBottom: 16, background: '#fafafa' }} bordered={false}>
        <Space>
          {productSku && <Tag color="blue">{productSku}</Tag>}
          <Text>{productName}</Text>
        </Space>
        <div style={{ marginTop: 4, fontSize: 12, color: '#666' }}>
          {inventory.batch && <span>{t('models.inventory.batch')}: {inventory.batch}</span>}
          {inventory.expiration_date && <span style={{ marginLeft: 12 }}>{t('models.inventory.expiration_date')}: {inventory.expiration_date}</span>}
        </div>
      </Card>

      {/* Shelf Table */}
      {shelfEntries.length === 0 && !loadingShelves ? (
        <Alert
          message={t('shared.stock_adjustment.error.all_shelves_adjusted')}
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      ) : (
        <Table
          columns={shelfColumns}
          dataSource={shelfEntries}
          rowKey="shelf_id"
          loading={loadingShelves}
          pagination={false}
          size="small"
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Shared Fields */}
      <Form form={form} layout="vertical">
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label={t('models.stock_adjustment.reason')} name="reason_code" rules={[{ required: true }]}>
              <Select>
                {REASON_CODES.map(code => (
                  <Select.Option key={code} value={code}>{t(`enums.stock_adjustment.reason_code.${code}`)}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label={t('models.stock_adjustment.note')} name="note">
              <Input.TextArea rows={2} placeholder={t('common.placeholder.note')} />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      {changedEntries.length > 0 && (
        <Alert
          message={t('shared.stock_adjustment.adjustment_summary', { count: changedEntries.length })}
          type="info"
          showIcon
        />
      )}
    </Modal>
  );
}
